import React, { useEffect, useMemo, useState } from 'react';
import { get, ref, set, runTransaction } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { database } from '../../firebase';
import { useGame } from '../../contexts/GameContext';
import AdvancedBattleSimulator from '../../battle/components/AdvancedBattleSimulator';
import { toCalcAbilityName } from '../../utils/abilityUtils';
import { getOwnedPokemonDisplayParts } from '../../utils/ownedPokemonDisplay';
import allPokemonMaster from '../../data/allPokemon.json';
import customBattleData from '../../data/customBattleData.json';
import { BATTLE_RULES, DEFAULT_BATTLE_RULE_ID, getBattleRule } from '../../battle/data/battleRules';

const BATTLE_LOG_ARCHIVE_KEY = 'poke-commu-battle-log-archive';
const MAX_BATTLE_LOG_ARCHIVE = 50;

const readBattleLogArchive = () => {
  if (typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(BATTLE_LOG_ARCHIVE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('배틀 로그 아카이브를 불러오지 못했습니다:', error);
    return [];
  }
};

const writeBattleLogArchive = (logs) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BATTLE_LOG_ARCHIVE_KEY, JSON.stringify(logs));
};

const TYPE_MAP = {
  노말: 'Normal',
  불꽃: 'Fire',
  물: 'Water',
  전기: 'Electric',
  풀: 'Grass',
  얼음: 'Ice',
  격투: 'Fighting',
  독: 'Poison',
  땅: 'Ground',
  비행: 'Flying',
  에스퍼: 'Psychic',
  벌레: 'Bug',
  바위: 'Rock',
  고스트: 'Ghost',
  드래곤: 'Dragon',
  악: 'Dark',
  강철: 'Steel',
  페어리: 'Fairy',
};

const normalizeType = type => TYPE_MAP[type] || type || 'Normal';

const normalizeLookupKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[\s_\-'.:]/g, '')
  .replace(/[^\p{L}\p{N}]/gu, '');

const CUSTOM_ABILITY_ALIASES = Object.entries(customBattleData.aliases?.abilities || {}).reduce((map, [key, value]) => {
  map[normalizeLookupKey(key)] = value;
  return map;
}, {});

const CUSTOM_ITEM_ALIASES = Object.entries(customBattleData.aliases?.items || {}).reduce((map, [key, value]) => {
  map[normalizeLookupKey(key)] = value;
  return map;
}, {});

const resolveCustomAbility = value =>
  CUSTOM_ABILITY_ALIASES[normalizeLookupKey(value)] || toCalcAbilityName(value) || value || 'Adaptability';

const resolveCustomItem = value =>
  CUSTOM_ITEM_ALIASES[normalizeLookupKey(value)] || value || '';

const POKEMON_NAME_MAP = allPokemonMaster.reduce((map, pokemon) => {
  [
    pokemon.name,
    pokemon.nameEn,
    pokemon.id,
    pokemon.number,
    pokemon.displayNumber,
  ].forEach((key) => {
    const normalizedKey = normalizeLookupKey(key);
    if (normalizedKey) map[normalizedKey] = pokemon;
  });
  return map;
}, {});

const resolvePokemonTemplate = (pokemon) => {
  const candidates = [
    pokemon.nameEn,
    pokemon.species,
    pokemon.name,
    pokemon.pokemonId,
    pokemon.id,
    pokemon.number,
    pokemon.originalNumber,
  ];

  return candidates
    .map(candidate => POKEMON_NAME_MAP[normalizeLookupKey(candidate)])
    .find(Boolean);
};

const toBattleMoveName = (move) => {
  if (!move) return null;
  if (typeof move === 'string') return move;
  return move.nameEn || move.name || move.id || move.moveId || null;
};

const calculateBattleHP = (baseHp, level, iv = 31, ev = 0) => (
  Math.floor(((2 * baseHp + iv + Math.floor(ev / 4)) * level) / 100) + level + 10
);

const calculateBattleStat = (baseStat, level, iv = 31, ev = 0) => (
  Math.floor(((2 * baseStat + iv + Math.floor(ev / 4)) * level) / 100) + 5
);

const toBattleStats = (pokemon, template) => {
  const level = Number(pokemon.level || 50);
  const hpIv = pokemon.ivs?.hp ?? pokemon.iv?.hp ?? 31;
  const hpEv = pokemon.effort?.hp ?? pokemon.evs?.hp ?? 0;
  const baseHp = Number(pokemon.baseHp || template?.baseHp || 50);

  return {
    hp: calculateBattleHP(baseHp, level, hpIv, hpEv),
    atk: calculateBattleStat(Number(pokemon.baseAttack || template?.baseAttack || 50), level, pokemon.ivs?.attack ?? pokemon.ivs?.atk ?? pokemon.iv?.attack ?? 31, pokemon.effort?.attack ?? pokemon.evs?.atk ?? 0),
    def: calculateBattleStat(Number(pokemon.baseDefense || template?.baseDefense || 50), level, pokemon.ivs?.defense ?? pokemon.ivs?.def ?? pokemon.iv?.defense ?? 31, pokemon.effort?.defense ?? pokemon.evs?.def ?? 0),
    spa: calculateBattleStat(Number(pokemon.baseSpAttack || pokemon.baseSpecialAttack || template?.baseSpAttack || template?.baseSpecialAttack || 50), level, pokemon.ivs?.specialAttack ?? pokemon.ivs?.spa ?? pokemon.iv?.specialAttack ?? 31, pokemon.effort?.specialAttack ?? pokemon.evs?.spa ?? 0),
    spd: calculateBattleStat(Number(pokemon.baseSpDefense || pokemon.baseSpecialDefense || template?.baseSpDefense || template?.baseSpecialDefense || 50), level, pokemon.ivs?.specialDefense ?? pokemon.ivs?.spd ?? pokemon.iv?.specialDefense ?? 31, pokemon.effort?.specialDefense ?? pokemon.evs?.spd ?? 0),
    spe: calculateBattleStat(Number(pokemon.baseSpeed || template?.baseSpeed || 50), level, pokemon.ivs?.speed ?? pokemon.ivs?.spe ?? pokemon.iv?.speed ?? 31, pokemon.effort?.speed ?? pokemon.evs?.spe ?? 0),
  };
};

const toBattleFormat = (pokemon) => {
  const template = resolvePokemonTemplate(pokemon);
  const speciesName = pokemon.nameEn || template?.nameEn || pokemon.species || pokemon.name || 'Ditto';
  const stats = toBattleStats(pokemon, template);
  const moves = (pokemon.moves || [])
    .map(toBattleMoveName)
    .filter(Boolean)
    .slice(0, 4)
    .map(name => ({ name, id: name }));

  return {
    ...pokemon,
    name: speciesName,
    species: speciesName,
    nickname: pokemon.nickname || pokemon.name || speciesName,
    speciesName: pokemon.name || template?.name || speciesName,
    level: Number(pokemon.level || 50),
    types: [
      normalizeType(pokemon.type || template?.type),
      pokemon.type2 || template?.type2 ? normalizeType(pokemon.type2 || template?.type2) : null,
    ].filter(Boolean),
    ability: resolveCustomAbility(pokemon.abilityEn || pokemon.ability),
    item: resolveCustomItem(pokemon.heldItem || pokemon.item || pokemon.heldItemName),
    nature: pokemon.nature || 'Hardy',
    stats,
    baseStats: stats,
    ivs: {
      hp: pokemon.ivs?.hp ?? pokemon.iv?.hp ?? 31,
      atk: pokemon.ivs?.attack ?? pokemon.ivs?.atk ?? pokemon.iv?.attack ?? 31,
      def: pokemon.ivs?.defense ?? pokemon.ivs?.def ?? pokemon.iv?.defense ?? 31,
      spa: pokemon.ivs?.specialAttack ?? pokemon.ivs?.spa ?? pokemon.iv?.specialAttack ?? 31,
      spd: pokemon.ivs?.specialDefense ?? pokemon.ivs?.spd ?? pokemon.iv?.specialDefense ?? 31,
      spe: pokemon.ivs?.speed ?? pokemon.ivs?.spe ?? pokemon.iv?.speed ?? 31,
    },
    evs: {
      hp: pokemon.effort?.hp ?? pokemon.evs?.hp ?? 0,
      atk: pokemon.effort?.attack ?? pokemon.evs?.atk ?? 0,
      def: pokemon.effort?.defense ?? pokemon.evs?.def ?? 0,
      spa: pokemon.effort?.specialAttack ?? pokemon.evs?.spa ?? 0,
      spd: pokemon.effort?.specialDefense ?? pokemon.evs?.spd ?? 0,
      spe: pokemon.effort?.speed ?? pokemon.evs?.spe ?? 0,
    },
    hp: stats.hp,
    maxHP: stats.hp,
    currentHP: stats.hp,
    moves: moves.length > 0 ? moves : [{ name: 'tackle', id: 'tackle' }],
  };
};

const getMemberName = member => member?.name || member?.displayName || member?.email || 'Unknown';

const getOwnedPokemonList = (value) => {
  if (Array.isArray(value)) return value.filter(pokemon => pokemon && pokemon.uniqueId);
  return Object.values(value || {}).filter(pokemon => pokemon && pokemon.uniqueId);
};

// Firebase RTDB drops null entries and returns a sparse array as a plain object
// (e.g. { "0": {...}, "3": {...} }) whenever indices aren't consecutive from 0.
// Rebuild the original index positions so slicing party slots (0-5) stays correct.
const normalizeCaughtPokemonByIndex = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  const result = [];
  Object.entries(value).forEach(([key, pokemon]) => {
    const index = Number(key);
    if (Number.isInteger(index) && index >= 0) result[index] = pokemon;
  });
  return result;
};

const pokemonKey = (pokemon, fallback) => pokemon?.uniqueId || pokemon?.id || `${pokemon?.name || 'pokemon'}-${fallback}`;

const getSelectablePokemon = (entryPokemon, partnerPokemon) => {
  const partnerKey = partnerPokemon?.uniqueId ? pokemonKey(partnerPokemon, 'partner') : null;
  const partnerList = partnerPokemon?.uniqueId ? [partnerPokemon] : [];
  const entryList = entryPokemon.filter((pokemon, index) => pokemonKey(pokemon, index) !== partnerKey);
  return [...partnerList, ...entryList];
};

const getSelectedPokemon = (entryPokemon, partnerPokemon, selectedIds) => {
  const selectablePokemon = getSelectablePokemon(entryPokemon, partnerPokemon);
  return selectedIds
    .map(id => selectablePokemon.find((pokemon, index) => pokemonKey(pokemon, index) === id))
    .filter(Boolean);
};

const buildBattleTeam = (entryPokemon, partnerPokemon, selectedIds) => (
  getSelectedPokemon(entryPokemon, partnerPokemon, selectedIds).map(toBattleFormat)
);

const PokemonName = ({ pokemon }) => {
  if (!pokemon) return <span>-</span>;
  const displayName = getOwnedPokemonDisplayParts(pokemon);

  return (
    <span>
      <span className="font-bold">{displayName.primary}</span>
      {displayName.hasNickname && <span className="ml-1 text-xs text-gray-500">{displayName.species}</span>}
    </span>
  );
};

const PartnerCard = ({ canSelect, onClick, order, pokemon, selected }) => (
  <button
    type="button"
    onClick={pokemon?.uniqueId ? onClick : undefined}
    disabled={!pokemon?.uniqueId || (!selected && !canSelect)}
    className={`w-full rounded-lg border p-3 text-left transition-colors ${
      selected
        ? 'border-amber-600 bg-amber-500 text-white'
        : pokemon?.uniqueId && canSelect
          ? 'border-amber-200 bg-amber-50 hover:border-amber-400 hover:bg-amber-100'
          : 'border-gray-200 bg-gray-50 text-gray-500'
    }`}
  >
    <div className={`mb-1 text-xs font-bold uppercase tracking-wide ${selected ? 'text-white' : 'text-amber-700'}`}>파트너</div>
    {pokemon?.uniqueId ? (
      <div className="flex items-center justify-between gap-2 text-sm">
        <div>
          <PokemonName pokemon={pokemon} /> <span className={selected ? 'text-white/80' : 'text-gray-600'}>Lv.{pokemon.level || '?'}</span>
        </div>
        {selected && (
          <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-gray-900">
            {order === 1 ? '선봉' : `${order}번`}
          </span>
        )}
      </div>
    ) : (
      <div className="text-sm text-gray-500">파트너 없음</div>
    )}
  </button>
);

const BattleSizePicker = ({ accent, size, onChange, min = 1 }) => {
  const activeClass = accent === 'blue' ? 'bg-blue-600 text-white' : 'bg-red-600 text-white';
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
      {[1, 2, 3, 4, 5, 6].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          disabled={n < min}
          title={n < min ? `이 룰은 최소 ${min}마리가 필요합니다` : undefined}
          className={`rounded-md px-2.5 py-1 text-xs font-bold ${
            size === n ? activeClass : n < min ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );
};

const EntrySelector = ({
  accent,
  entryPokemon,
  loading,
  partnerPokemon,
  requiredCount,
  selectedIds,
  setSelectedIds,
}) => {
  const partnerKey = partnerPokemon?.uniqueId ? pokemonKey(partnerPokemon, 'partner') : null;
  const partnerOrder = partnerKey ? selectedIds.indexOf(partnerKey) + 1 : 0;
  const partnerSelected = partnerOrder > 0;
  const entryList = partnerKey
    ? entryPokemon.filter((pokemon, index) => pokemonKey(pokemon, index) !== partnerKey)
    : entryPokemon;
  const accentClass = accent === 'blue'
    ? {
        border: 'border-blue-500 bg-blue-50',
        selected: 'border-blue-600 bg-blue-600 text-white',
        idle: 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-100',
        text: 'text-blue-900',
      }
    : {
        border: 'border-red-500 bg-red-50',
        selected: 'border-red-600 bg-red-600 text-white',
        idle: 'border-gray-200 bg-white hover:border-red-300 hover:bg-red-100',
        text: 'text-red-900',
      };

  const togglePokemon = (pokemon, index) => {
    const key = pokemonKey(pokemon, index);
    setSelectedIds((prev) => {
      if (prev.includes(key)) return prev.filter(id => id !== key);
      if (prev.length >= requiredCount) return prev;
      return [...prev, key];
    });
  };

  return (
    <div className={`rounded-lg border-2 p-5 ${accentClass.border}`}>
      <PartnerCard
        canSelect={selectedIds.length < requiredCount}
        onClick={() => togglePokemon(partnerPokemon, 'partner')}
        order={partnerOrder}
        pokemon={partnerPokemon}
        selected={partnerSelected}
      />

      <div className="mt-4 flex items-center justify-between gap-3">
        <h3 className={`font-bold ${accentClass.text}`}>엔트리 선택</h3>
        <span className="text-sm font-semibold text-gray-600">
          {selectedIds.length}/{requiredCount}
        </span>
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-gray-500">포켓몬을 불러오는 중입니다.</p>
      ) : entryList.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">엔트리에 포켓몬이 없습니다.</p>
      ) : (
        <div className="mt-3 grid max-h-[28rem] gap-2 overflow-y-auto">
          {entryList.map((pokemon, index) => {
            const key = pokemonKey(pokemon, index);
            const order = selectedIds.indexOf(key) + 1;
            const selected = order > 0;
            const displayName = getOwnedPokemonDisplayParts(pokemon);

            return (
              <button
                key={key}
                type="button"
                onClick={() => togglePokemon(pokemon, index)}
                className={`rounded-lg border p-3 text-left transition-colors ${selected ? accentClass.selected : accentClass.idle}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold">
                      {displayName.primary} <span className="text-sm opacity-80">Lv.{pokemon.level || '?'}</span>
                    </div>
                    {displayName.hasNickname && (
                      <div className="text-xs opacity-75">{displayName.species}</div>
                    )}
                    <div className="mt-1 text-xs opacity-80">
                      HP {pokemon.hp || pokemon.maxHp || '?'} · 기술 {pokemon.moves?.length || 0}개
                    </div>
                  </div>
                  {selected && (
                    <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-gray-900">
                      {order === 1 ? '선봉' : `${order}번`}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const formatBattleLogDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BattleLogArchiveModal = ({ logs, onClose, onDelete, loading }) => {
  const [selectedId, setSelectedId] = useState(logs[0]?.id || null);
  const selectedLog = logs.find(log => log.id === selectedId) || logs[0] || null;
  const [collapsedDates, setCollapsedDates] = useState({});
  const [shareInput, setShareInput] = useState('');
  const [sharedLog, setSharedLog] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyId = (id, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const handleLoadShared = async () => {
    const id = shareInput.trim();
    if (!id) return;
    setShareLoading(true);
    try {
      const snap = await get(ref(database, `battleLogs/${id}`));
      if (snap.exists()) {
        setSharedLog(snap.val());
      } else {
        alert('해당 ID의 배틀 로그를 찾을 수 없습니다.');
      }
    } catch {
      alert('로드 실패. ID를 다시 확인해주세요.');
    } finally {
      setShareLoading(false);
    }
  };

  const displayLog = sharedLog || selectedLog;

  useEffect(() => {
    setSelectedId(logs[0]?.id || null);
  }, [logs]);

  const dateGroups = useMemo(() => {
    const groups = {};
    [...logs].sort((a, b) => b.createdAt - a.createdAt).forEach(log => {
      const date = new Date(log.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
      if (!groups[date]) groups[date] = [];
      groups[date].push(log);
    });
    return Object.entries(groups);
  }, [logs]);

  const toggleDate = (date) => setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-4" style={{ zIndex: 9000 }}>
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">배틀 로그</h2>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={shareInput}
                onChange={e => { setShareInput(e.target.value); setSharedLog(null); }}
                placeholder="공유 ID 입력..."
                className="rounded border border-gray-300 px-3 py-1.5 text-sm w-64 outline-none focus:border-gray-500"
                onKeyDown={e => e.key === 'Enter' && handleLoadShared()}
              />
              <button
                type="button"
                onClick={handleLoadShared}
                disabled={shareLoading}
                className="rounded bg-gray-700 px-3 py-1.5 text-sm font-bold text-white hover:bg-gray-600 disabled:opacity-50"
              >
                {shareLoading ? '...' : '불러오기'}
              </button>
              {sharedLog && (
                <button type="button" onClick={() => { setSharedLog(null); setShareInput(''); }} className="rounded bg-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-300">내 기록으로</button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white hover:bg-gray-700"
          >
            닫기
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">로그 불러오는 중...</div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-gray-500">아직 저장된 배틀 로그가 없습니다.</div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)]">
            <div className="min-h-0 overflow-y-auto border-r border-gray-200 bg-gray-50 p-3">
              {dateGroups.map(([date, dateLogs]) => (
                <div key={date} className="mb-2">
                  <button
                    type="button"
                    onClick={() => toggleDate(date)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs font-bold text-gray-500 hover:bg-gray-200 transition-colors"
                  >
                    <span>{date}</span>
                    <span className="text-gray-400">{collapsedDates[date] ? '▶' : '▼'} {dateLogs.length}건</span>
                  </button>
                  {!collapsedDates[date] && dateLogs.map(log => (
                    <div key={log.id} className="relative mt-1 mb-1 group">
                      <button
                        type="button"
                        onClick={() => setSelectedId(log.id)}
                        className={`w-full rounded-lg border px-3 py-3 text-left transition-colors pr-8 ${
                          selectedLog?.id === log.id
                            ? 'border-transparent text-white shadow-sm'
                            : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white'
                        }`}
                        style={selectedLog?.id === log.id ? { backgroundColor: '#2d4a35' } : {}}
                      >
                        <div className="text-sm font-bold">{log.player1Name} vs {log.player2Name}</div>
                        <div className="mt-1 text-xs text-gray-500">
                          {formatBattleLogDate(log.createdAt)} · {log.turn}턴 · {log.winner || '-'} 승리
                        </div>
                      </button>
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => handleCopyId(log.id, e)}
                          className="rounded p-0.5 text-xs text-gray-400 hover:text-blue-500 hover:bg-blue-50"
                          title="ID 복사"
                        >
                          {copiedId === log.id ? '✓' : '🔗'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDelete(log.id); }}
                          className="rounded p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50"
                          title="삭제"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="min-h-0 overflow-y-auto p-5">
              {displayLog && (
                <>
                  <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-lg font-bold text-gray-900">
                        {displayLog.player1Name} vs {displayLog.player2Name}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleCopyId(displayLog.id, e)}
                        className="flex-shrink-0 rounded bg-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-blue-100 hover:text-blue-700"
                        title="ID 복사"
                      >
                        {copiedId === displayLog.id ? '✓ 복사됨' : '🔗 ID 공유'}
                      </button>
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {formatBattleLogDate(displayLog.createdAt)} · 총 {displayLog.turn}턴 · 승자: {displayLog.winner || '-'}
                    </div>
                    <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                      <div>
                        <div className="font-bold text-blue-800">Player 1</div>
                        <div className="text-gray-700">{displayLog.player1Team?.join(', ') || '-'}</div>
                      </div>
                      <div>
                        <div className="font-bold text-red-800">Player 2</div>
                        <div className="text-gray-700">{displayLog.player2Team?.join(', ') || '-'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1 rounded-lg bg-gray-50 p-4">
                    {(displayLog.log || []).map((entry, index) => (
                      <div
                        key={`${entry.type}-${entry.message}-${index}`}
                        className={`rounded px-3 py-2 text-sm ${
                          entry.type === 'turn'        ? 'bg-gray-700 font-bold text-white text-center tracking-widest'
                          : entry.type === 'system'      ? 'bg-blue-100 font-bold text-blue-900'
                          : entry.type === 'damage'    ? 'bg-red-50 text-red-800'
                          : entry.type === 'faint'     ? 'bg-gray-200 font-semibold text-gray-700'
                          : entry.type === 'winner'    ? 'bg-yellow-100 text-lg font-bold text-yellow-900'
                          : entry.type === 'item'      ? 'bg-amber-50 text-amber-800'
                          : entry.type === 'status'    ? 'bg-yellow-50 text-yellow-800 font-medium'
                          : entry.type === 'boost'     ? 'bg-indigo-50 text-indigo-700'
                          : entry.type === 'weather'   ? 'bg-sky-50 text-sky-700'
                          : entry.type === 'terrain'   ? 'bg-green-50 text-green-700'
                          : entry.type === 'critical'  ? 'bg-orange-50 text-orange-700 font-semibold'
                          : entry.type === 'miss'      ? 'bg-gray-100 text-gray-500 italic'
                          : entry.type === 'fail'      ? 'bg-gray-100 text-gray-500 italic'
                          : entry.type === 'recoil'    ? 'bg-red-50 text-red-600'
                          : entry.type === 'drain'     ? 'bg-green-50 text-green-600'
                          : entry.type === 'blocked'   ? 'bg-gray-100 text-gray-500 italic'
                          : entry.type === 'error'     ? 'bg-red-100 text-red-700 font-semibold'
                          : 'bg-white text-gray-700'
                        }`}
                      >
                        {entry.message}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export function BattleView() {
  const { checkEvolutionOnLevelUp, allItems } = useGame();
  const [battleItemsEnabled, setBattleItemsEnabled] = useState(false);
  const [battleRuleId, setBattleRuleId] = useState(DEFAULT_BATTLE_RULE_ID);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingPokemon, setLoadingPokemon] = useState({ player1: false, player2: false });
  const [members, setMembers] = useState([]);
  // 양 진영이 서로 다른 마리 수로 배틀할 수 있도록(2:3, 4:3, 6:5 등) 각자 독립적으로 갖는다.
  const [battleSizeP1, setBattleSizeP1] = useState(3);
  const [battleSizeP2, setBattleSizeP2] = useState(3);
  const [selectedUser1, setSelectedUser1] = useState('');
  const [selectedUser2, setSelectedUser2] = useState('');
  const [player1Data, setPlayer1Data] = useState({ entryPokemon: [], partnerPokemon: null, inventory: [] });
  const [player2Data, setPlayer2Data] = useState({ entryPokemon: [], partnerPokemon: null, inventory: [] });
  const [selectedP1Ids, setSelectedP1Ids] = useState([]);
  const [selectedP2Ids, setSelectedP2Ids] = useState([]);
  const [battleStarted, setBattleStarted] = useState(false);
  const [battleLogArchive, setBattleLogArchive] = useState(() => readBattleLogArchive());
  const [showBattleLogArchive, setShowBattleLogArchive] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  // 배틀 중 소모된 아이템 추적 (uid → { itemId → count })

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);

        const membersSnapshot = await get(ref(database, 'members'));
        let membersList = [];

        if (membersSnapshot.exists()) {
          membersList = Object.entries(membersSnapshot.val())
            .filter(([, data]) => data !== null)
            .map(([uid, data]) => ({ uid, ...data }));
        }

        const usersSnapshot = await get(ref(database, 'users'));
        if (usersSnapshot.exists()) {
          const existingUids = membersList.map(member => member.uid);
          const additionalUsers = Object.entries(usersSnapshot.val())
            .filter(([uid]) => !existingUids.includes(uid))
            .map(([uid, data]) => ({
              uid,
              name: data.name || data.displayName || data.email || uid.substring(0, 8),
              email: data.email,
            }));
          membersList = [...membersList, ...additionalUsers];
        }

        setMembers(membersList.sort((a, b) => getMemberName(a).localeCompare(getMemberName(b), 'ko')));
      } catch (error) {
        console.error('회원 로드 실패:', error);
      } finally {
        setLoadingMembers(false);
      }
    };

    loadMembers();
  }, []);

  const loadPlayerPokemon = async (player, userId) => {
    const setUser = player === 'player1' ? setSelectedUser1 : setSelectedUser2;
    const setData = player === 'player1' ? setPlayer1Data : setPlayer2Data;
    const setSelectedIds = player === 'player1' ? setSelectedP1Ids : setSelectedP2Ids;

    setUser(userId);
    setData({ entryPokemon: [], partnerPokemon: null });
    setSelectedIds([]);
    setBattleStarted(false);

    if (!userId) return;

    try {
      setLoadingPokemon(prev => ({ ...prev, [player]: true }));
      const snapshot = await get(ref(database, `members/${userId}`));

      if (!snapshot.exists()) return;

      const memberData = snapshot.val();
      const caughtPokemon = normalizeCaughtPokemonByIndex(memberData.caughtPokemon);
      const entryPokemon = getOwnedPokemonList(caughtPokemon.slice(0, 6));

      setData({
        entryPokemon,
        partnerPokemon: memberData.partnerPokemon || null,
        inventory: Array.isArray(memberData.inventory) ? memberData.inventory : [],
      });
    } catch (error) {
      console.error('포켓몬 로드 실패:', error);
    } finally {
      setLoadingPokemon(prev => ({ ...prev, [player]: false }));
    }
  };

  useEffect(() => {
    const p1SelectableIds = getSelectablePokemon(player1Data.entryPokemon, player1Data.partnerPokemon)
      .map((pokemon, index) => pokemonKey(pokemon, index));
    const p2SelectableIds = getSelectablePokemon(player2Data.entryPokemon, player2Data.partnerPokemon)
      .map((pokemon, index) => pokemonKey(pokemon, index));

    setSelectedP1Ids(prev => prev.filter(id => p1SelectableIds.includes(id)).slice(0, Math.min(battleSizeP1, p1SelectableIds.length)));
    setSelectedP2Ids(prev => prev.filter(id => p2SelectableIds.includes(id)).slice(0, Math.min(battleSizeP2, p2SelectableIds.length)));
  }, [battleSizeP1, battleSizeP2, player1Data.entryPokemon, player1Data.partnerPokemon, player2Data.entryPokemon, player2Data.partnerPokemon]);

  // 더블배틀 등 동시 출전 마리 수(activeCount)가 2 이상인 룰은 양쪽 모두 최소 그만큼
  // 엔트리가 있어야 한다 — 부족한 채로 시작하면 배틀 엔진이 그대로 죽는다.
  const requiredActiveCount = getBattleRule(battleRuleId).activeCount || 1;

  useEffect(() => {
    if (requiredActiveCount <= 1) return;
    setBattleSizeP1(prev => Math.max(prev, requiredActiveCount));
    setBattleSizeP2(prev => Math.max(prev, requiredActiveCount));
  }, [requiredActiveCount]);

  const p1RequiredCount = Math.min(battleSizeP1, getSelectablePokemon(player1Data.entryPokemon, player1Data.partnerPokemon).length);
  const p2RequiredCount = Math.min(battleSizeP2, getSelectablePokemon(player2Data.entryPokemon, player2Data.partnerPokemon).length);
  const canStart = p1RequiredCount >= requiredActiveCount
    && p2RequiredCount >= requiredActiveCount
    && selectedP1Ids.length === p1RequiredCount
    && selectedP2Ids.length === p2RequiredCount;

  const player1Team = useMemo(
    () => buildBattleTeam(player1Data.entryPokemon, player1Data.partnerPokemon, selectedP1Ids),
    [player1Data.entryPokemon, player1Data.partnerPokemon, selectedP1Ids]
  );
  const player2Team = useMemo(
    () => buildBattleTeam(player2Data.entryPokemon, player2Data.partnerPokemon, selectedP2Ids),
    [player2Data.entryPokemon, player2Data.partnerPokemon, selectedP2Ids]
  );

  const player1Name = getMemberName(members.find(member => member.uid === selectedUser1));
  const player2Name = getMemberName(members.find(member => member.uid === selectedUser2));

  const handleBattleFinished = async (battleSummary) => {
    const archiveEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: new Date().toISOString(),
      player1Name,
      player2Name,
      player1Team: player1Team.map(pokemon => getOwnedPokemonDisplayParts(pokemon).primary),
      player2Team: player2Team.map(pokemon => getOwnedPokemonDisplayParts(pokemon).primary),
      ...battleSummary,
    };

    setBattleLogArchive((prev) => {
      const next = [archiveEntry, ...prev].slice(0, MAX_BATTLE_LOG_ARCHIVE);
      writeBattleLogArchive(next);
      return next;
    });

    // Firebase에 저장 (유저별 + 전체 공용)
    try {
      const uid = getAuth().currentUser?.uid;
      if (uid) {
        await set(ref(database, `battleLogs/${uid}/${archiveEntry.id}`), archiveEntry);
      }
    } catch (e) {
      console.warn('배틀 로그 Firebase 저장 실패:', e);
    }

    // moveUsage / lastBattleCritCount / 지닌 도구(held item) 소모 업데이트
    const rawLog = battleSummary.rawLog || [];
    if (rawLog.length > 0) {
      const normalizeId = v => String(v || '').toLowerCase().replace(/[\s_\-'.:]/g, '').replace(/[^a-z0-9぀-鿿가-힣]/g, '');
      const pokemonKey = p => p?.uniqueId || p?.id || p?.pokemonId || `${p?.number}_${p?.name}`;

      // 슬롯 → { memberId, pokemon } 매핑 (선택된 1번 포켓몬 기준)
      const slotMap = {
        p1a: { memberId: selectedUser1, pokemon: player1Team[0] },
        p2a: { memberId: selectedUser2, pokemon: player2Team[0] },
      };

      const usage = {};   // { memberId: { key: { moveId: count } } }
      const crits = {};   // { memberId: { key: count } }
      // 배틀 중 나무열매를 "먹어서"(-enditem [eat]) 실제로 소모된 포켓몬만 담는다.
      // 포커스 샤시가 깨지거나 초킹아이템이 상대 기술로 떨어지는 등 나무열매가 아닌
      // 지닌 도구 소모는 배틀 안에서만 효과가 나고, 실제 보유 아이템은 배틀 종료 후에도
      // 남아있어야 한다 - 멤버가 직접 회수하지 않는 이상 사라지지 않음.
      const heldItemUsed = {}; // { memberId: { key: true } }
      const isBerryItemName = (rawName) => {
        const norm = normalizeId(rawName);
        if (!norm) return false;
        return (allItems || []).some(it => {
          const nameMatch = normalizeId(it.name) === norm || normalizeId(it.nameEn) === norm;
          if (!nameMatch) return false;
          return it.categoryData?.pocket === 'berries' || (it.nameEn || '').endsWith('-berry');
        });
      };
      let lastMoveSlot = null;

      for (const line of rawLog) {
        if (!line) continue;
        const parts = line.split('|');
        if (line.startsWith('|move|')) {
          const slot = (parts[2] || '').split(':')[0].trim().toLowerCase();
          const moveName = (parts[3] || '').trim();
          lastMoveSlot = slot;
          if (!moveName || moveName === 'Struggle') continue;
          const info = slotMap[slot];
          if (!info?.memberId || !info?.pokemon) continue;
          const key = pokemonKey(info.pokemon);
          const mid = normalizeId(moveName);
          if (!usage[info.memberId]) usage[info.memberId] = {};
          if (!usage[info.memberId][key]) usage[info.memberId][key] = {};
          usage[info.memberId][key][mid] = (usage[info.memberId][key][mid] || 0) + 1;
        }
        if (line.startsWith('|-crit|') && lastMoveSlot) {
          const info = slotMap[lastMoveSlot];
          if (!info?.memberId || !info?.pokemon) continue;
          const key = pokemonKey(info.pokemon);
          if (!crits[info.memberId]) crits[info.memberId] = {};
          crits[info.memberId][key] = (crits[info.memberId][key] || 0) + 1;
        }
        if (line.startsWith('|-enditem|') && parts.includes('[eat]')) {
          const slot = (parts[2] || '').split(':')[0].trim().toLowerCase();
          const itemName = (parts[3] || '').trim();
          const info = slotMap[slot];
          if (!info?.memberId || !info?.pokemon) continue;
          if (!isBerryItemName(itemName)) continue;
          const key = pokemonKey(info.pokemon);
          if (!heldItemUsed[info.memberId]) heldItemUsed[info.memberId] = {};
          heldItemUsed[info.memberId][key] = true;
        }
      }

      const clearHeldItem = (p) => ({ ...p, heldItem: null, item: null, heldItemName: null });

      const memberIds = new Set([...Object.keys(usage), ...Object.keys(crits), ...Object.keys(heldItemUsed)]);
      await Promise.all([...memberIds].map(async (memberId) => {
        try {
          // NPC 트레이너의 포켓몬은 배틀에서 나무열매를 "먹어도" 실제 보유 아이템에서
          // 소모 처리하지 않는다 - NPC는 플레이어가 직접 채워준 지닌 도구를 회수할 방법이
          // 없으므로, 매번 배틀할 때마다 열매가 사라지면 관리자가 계속 다시 채워줘야 한다.
          const isNpcSnap = await get(ref(database, `members/${memberId}/isNPC`));
          const isNpc = !!isNpcSnap.val();

          const snap = await get(ref(database, `members/${memberId}/caughtPokemon`));
          const rawCaught = snap.val();

          if (rawCaught) {
            const caught = Array.isArray(rawCaught)
              ? rawCaught
              : Object.assign(Array(Math.max(...Object.keys(rawCaught).map(Number)) + 1), rawCaught);

            const updated = caught.map(p => {
              if (!p) return p;
              const key = pokemonKey(p);
              let next = p;
              if (usage[memberId]?.[key]) {
                const merged = { ...(next.moveUsage || {}) };
                for (const [mid, cnt] of Object.entries(usage[memberId][key])) {
                  merged[mid] = (merged[mid] || 0) + cnt;
                }
                next = { ...next, moveUsage: merged };
              }
              if (crits[memberId]?.[key]) {
                next = { ...next, lastBattleCritCount: crits[memberId][key] };
              } else if (next.lastBattleCritCount !== undefined) {
                next = { ...next, lastBattleCritCount: 0 };
              }
              if (!isNpc && heldItemUsed[memberId]?.[key] && (next.heldItem || next.item || next.heldItemName)) {
                next = clearHeldItem(next);
              }
              return next;
            });

            await set(ref(database, `members/${memberId}/caughtPokemon`), updated);

            // 배틀 후 진화 체크 (battleCrit 조건 등)
            if (checkEvolutionOnLevelUp) {
              for (const p of updated) {
                if (!p) continue;
                const key = pokemonKey(p);
                if (crits[memberId]?.[key]) {
                  checkEvolutionOnLevelUp(p);
                }
              }
            }
          }

          // 파트너 포켓몬은 caughtPokemon 배열이 아니라 별도 경로에 저장되므로 따로 확인한다.
          if (!isNpc && heldItemUsed[memberId]) {
            const partnerSnap = await get(ref(database, `members/${memberId}/partnerPokemon`));
            const partner = partnerSnap.val();
            if (partner) {
              const key = pokemonKey(partner);
              if (heldItemUsed[memberId][key] && (partner.heldItem || partner.item || partner.heldItemName)) {
                await set(ref(database, `members/${memberId}/partnerPokemon`), clearHeldItem(partner));
              }
            }
          }
        } catch (e) {
          console.warn('moveUsage/지닌도구 업데이트 실패:', memberId, e);
        }
      }));
    }
  };

  // 배틀 중 아이템 소모 처리 (로컬 인벤토리 차감 + Firebase 저장)
  const handleConsumeItem = async (player, item) => {
    const userId = player === 'player1' ? selectedUser1 : selectedUser2;
    const setData = player === 'player1' ? setPlayer1Data : setPlayer2Data;

    const matchItem = (i) =>
      (item.itemId != null && i.itemId === item.itemId) ||
      (item.nameEn && i.nameEn === item.nameEn) ||
      (item.name && i.name === item.name);

    const decrementItem = (i) => {
      const cur = i.count ?? i.quantity ?? 1;
      return { ...i, count: cur - 1, quantity: cur - 1 };
    };

    // 로컬 인벤토리에서 수량 차감
    setData(prev => {
      const newInventory = prev.inventory
        .map(i => matchItem(i) ? decrementItem(i) : i)
        .filter(i => (i.count ?? i.quantity ?? 1) > 0);
      return { ...prev, inventory: newInventory };
    });

    // Firebase 저장
    if (!userId) return;
    try {
      await runTransaction(ref(database, `members/${userId}/inventory`), (currentInventory) => {
        const inv = Array.isArray(currentInventory) ? currentInventory : [];
        return inv
          .map(i => matchItem(i) ? decrementItem(i) : i)
          .filter(i => (i.count ?? i.quantity ?? 1) > 0);
      });
    } catch (e) {
      console.warn('아이템 소모 저장 실패:', e);
    }
  };

  const handleReturnItem = async (player, item) => {
    const userId = player === 'player1' ? selectedUser1 : selectedUser2;
    const setData = player === 'player1' ? setPlayer1Data : setPlayer2Data;

    const matchItem = (i) =>
      (item.itemId != null && i.itemId === item.itemId) ||
      (item.nameEn && i.nameEn === item.nameEn) ||
      (item.name && i.name === item.name);

    // 로컬 인벤토리에 수량 복원
    setData(prev => {
      const exists = prev.inventory.some(matchItem);
      const newInventory = exists
        ? prev.inventory.map(i => {
            if (!matchItem(i)) return i;
            const cur = i.count ?? i.quantity ?? 0;
            return { ...i, count: cur + 1, quantity: cur + 1 };
          })
        : [...prev.inventory, { ...item, count: 1, quantity: 1 }];
      return { ...prev, inventory: newInventory };
    });

    // Firebase 저장
    if (!userId) return;
    try {
      await runTransaction(ref(database, `members/${userId}/inventory`), (currentInventory) => {
        const inv = Array.isArray(currentInventory) ? currentInventory : [];
        const exists = inv.some(matchItem);
        return exists
          ? inv.map(i => {
              if (!matchItem(i)) return i;
              const cur = i.count ?? i.quantity ?? 0;
              return { ...i, count: cur + 1, quantity: cur + 1 };
            })
          : [...inv, { ...item, count: 1, quantity: 1 }];
      });
    } catch (e) {
      console.warn('아이템 반환 저장 실패:', e);
    }
  };

  const handleDeleteBattleLog = (id) => {
    setBattleLogArchive((prev) => {
      const next = prev.filter(log => log.id !== id);
      writeBattleLogArchive(next);
      return next;
    });
  };

  const handleOpenLogs = async () => {
    setShowBattleLogArchive(true);
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    setLogsLoading(true);
    try {
      const snap = await get(ref(database, `battleLogs/${uid}`));
      if (snap.exists()) {
        const data = snap.val();
        const fbLogs = Object.values(data).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setBattleLogArchive(fbLogs);
        writeBattleLogArchive(fbLogs.slice(0, MAX_BATTLE_LOG_ARCHIVE));
      }
    } catch (e) {
      console.warn('배틀 로그 Firebase 로드 실패:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  const renderBattleLogArchiveButton = () => (
    <button
      type="button"
      onClick={handleOpenLogs}
      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-800 shadow-sm hover:bg-gray-100"
    >
      로그
      <span className="ml-2 rounded-full bg-gray-900 px-2 py-0.5 text-xs text-white">{battleLogArchive.length}</span>
    </button>
  );

  if (battleStarted && player1Team.length > 0 && player2Team.length > 0) {
    return (
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setBattleStarted(false)}
            className="rounded bg-gray-600 px-4 py-2 font-semibold text-white hover:bg-gray-700"
          >
            엔트리 다시 고르기
          </button>
          {renderBattleLogArchiveButton()}
        </div>
        <AdvancedBattleSimulator
          player1Team={player1Team}
          player2Team={player2Team}
          ruleId={battleRuleId}
          autoStart
          onBattleFinished={handleBattleFinished}
          onExit={() => setBattleStarted(false)}
          battleItemsEnabled={battleItemsEnabled}
          player1Inventory={player1Data.inventory}
          player2Inventory={player2Data.inventory}
          onConsumeItem={handleConsumeItem}
          onReturnItem={handleReturnItem}
        />
        {showBattleLogArchive && (
          <BattleLogArchiveModal logs={battleLogArchive} onClose={() => setShowBattleLogArchive(false)} onDelete={handleDeleteBattleLog} loading={logsLoading} />
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">포켓몬 배틀</h1>
          <p className="mt-1 text-sm text-gray-600">파트너는 별도 표시하고, 배틀에는 각 트레이너의 엔트리 앞 6마리 중 선택한 포켓몬만 참가합니다.</p>
          {battleRuleId !== DEFAULT_BATTLE_RULE_ID && (
            <p className="mt-1 text-sm font-semibold text-purple-700">
              {BATTLE_RULES.find(rule => rule.id === battleRuleId)?.name}: {BATTLE_RULES.find(rule => rule.id === battleRuleId)?.description}
            </p>
          )}
          {battleItemsEnabled && requiredActiveCount <= 1 && (
            <p className="mt-1 text-sm font-semibold text-indigo-700">
              오리진 난이도: 배틀 중 아이템을 사용할 수 있고, 모든 포켓몬의 기초포인트는 0으로 취급되어 배틀에 영향을 주지 않습니다.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {renderBattleLogArchiveButton()}
          {/* 배틀 룰 선택 */}
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            룰
            <select
              value={battleRuleId}
              onChange={event => setBattleRuleId(event.target.value)}
              title={BATTLE_RULES.find(rule => rule.id === battleRuleId)?.description}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm"
            >
              {BATTLE_RULES.map(rule => (
                <option key={rule.id} value={rule.id}>{rule.name}</option>
              ))}
            </select>
          </label>
          {/* 오리진 난이도 토글 (배틀 중 아이템 사용 + 기초포인트 미반영, 더블배틀은 아직 미지원) */}
          <button
            type="button"
            onClick={() => setBattleItemsEnabled(prev => !prev)}
            disabled={requiredActiveCount > 1}
            title={
              requiredActiveCount > 1
                ? '더블배틀에서는 아직 오리진 난이도를 지원하지 않습니다.'
                : '배틀 중 아이템을 사용할 수 있고, 모든 포켓몬의 기초포인트가 배틀에 반영되지 않습니다.'
            }
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
              requiredActiveCount > 1
                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                : battleItemsEnabled
                  ? 'border-indigo-400 bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            🎒 오리진 난이도 {requiredActiveCount > 1 ? '(더블 미지원)' : battleItemsEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {loadingMembers ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-600 shadow">회원 데이터를 불러오는 중입니다.</div>
      ) : members.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-600 shadow">회원이 없습니다.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="mb-2 block font-semibold text-gray-800">Player 1 트레이너</label>
                <select
                  value={selectedUser1}
                  onChange={event => loadPlayerPokemon('player1', event.target.value)}
                  className="w-full rounded border bg-white p-2"
                >
                  <option value="">트레이너 선택</option>
                  {members.map(member => (
                    <option key={member.uid} value={member.uid}>{getMemberName(member)}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">참가 마리 수</span>
                <BattleSizePicker accent="blue" size={battleSizeP1} onChange={setBattleSizeP1} min={requiredActiveCount} />
              </div>
              {selectedUser1 && (
                <EntrySelector
                  accent="blue"
                  entryPokemon={player1Data.entryPokemon}
                  loading={loadingPokemon.player1}
                  partnerPokemon={player1Data.partnerPokemon}
                  requiredCount={p1RequiredCount}
                  selectedIds={selectedP1Ids}
                  setSelectedIds={setSelectedP1Ids}
                />
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block font-semibold text-gray-800">Player 2 트레이너</label>
                <select
                  value={selectedUser2}
                  onChange={event => loadPlayerPokemon('player2', event.target.value)}
                  className="w-full rounded border bg-white p-2"
                >
                  <option value="">트레이너 선택</option>
                  {members.map(member => (
                    <option key={member.uid} value={member.uid}>{getMemberName(member)}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">참가 마리 수</span>
                <BattleSizePicker accent="red" size={battleSizeP2} onChange={setBattleSizeP2} min={requiredActiveCount} />
              </div>
              {selectedUser2 && (
                <EntrySelector
                  accent="red"
                  entryPokemon={player2Data.entryPokemon}
                  loading={loadingPokemon.player2}
                  partnerPokemon={player2Data.partnerPokemon}
                  requiredCount={p2RequiredCount}
                  selectedIds={selectedP2Ids}
                  setSelectedIds={setSelectedP2Ids}
                />
              )}
            </div>
          </div>

          <div className="mt-8 rounded-lg bg-gray-50 p-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="text-sm font-semibold text-gray-600">Player 1 선봉{requiredActiveCount > 1 ? ` (${requiredActiveCount}마리)` : ''}</div>
                <div className="mt-1 flex flex-wrap gap-x-2 text-gray-900">
                  {getSelectedPokemon(player1Data.entryPokemon, player1Data.partnerPokemon, selectedP1Ids)
                    .slice(0, requiredActiveCount)
                    .map((pokemon, idx) => <PokemonName key={pokemon?.uniqueId || idx} pokemon={pokemon} />)}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-600">Player 2 선봉{requiredActiveCount > 1 ? ` (${requiredActiveCount}마리)` : ''}</div>
                <div className="mt-1 flex flex-wrap gap-x-2 text-gray-900">
                  {getSelectedPokemon(player2Data.entryPokemon, player2Data.partnerPokemon, selectedP2Ids)
                    .slice(0, requiredActiveCount)
                    .map((pokemon, idx) => <PokemonName key={pokemon?.uniqueId || idx} pokemon={pokemon} />)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setBattleStarted(true)}
              disabled={!canStart}
              className={`rounded-lg px-8 py-4 text-xl font-bold transition-colors ${
                canStart
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              배틀 시작
            </button>
            {!canStart && (
              <p className="mt-2 text-sm text-gray-500">
                {requiredActiveCount > 1 && (p1RequiredCount < requiredActiveCount || p2RequiredCount < requiredActiveCount)
                  ? `더블배틀은 양쪽 모두 최소 ${requiredActiveCount}마리 엔트리가 필요합니다.`
                  : '양쪽 모두 필요한 엔트리 수만큼 선택해야 합니다.'}
              </p>
            )}
          </div>
        </>
      )}
      {showBattleLogArchive && (
        <BattleLogArchiveModal logs={battleLogArchive} onClose={() => setShowBattleLogArchive(false)} onDelete={handleDeleteBattleLog} loading={logsLoading} />
      )}
    </div>
  );
}

export default BattleView;
